import {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import parseValidators from './core/parseValidators';
import validationProcessor from './core/validationProcessor';
import FormContext from './FormContext';

export const generateErrors = (validators, data, name) => {
  const validatorsForData = name ? { [name]: validators[name] } : { ...validators };
  const parsedValidators = parseValidators.getValidators(validatorsForData);
  return validationProcessor.runValidators(data, parsedValidators).then(
    value => Object.keys(parsedValidators).reduce(
      (memo, key) => ({ ...memo, [key]: (value && value[key]) || [] }),
      {}
    )
  );
};

const getIsValid = errors => !Object.values(errors)
  .filter((value) => (
    (Array.isArray(value) && value.filter(Boolean).length && typeof value[0] !== 'object')
  )).length;

/**
 * In here so that the identity of the initialData and initialError objects don't
 * change when the useValidation hook reruns
 */
const emptyObject = {};

/**
 * A custom React hook that manages state for a validated form or form section.
 *
 * The return value is an array containing the FormContext Provider component, the value to pass to
 * the Provider, and a copy of the value for ease of destructuring when those values are needed.
 * @param {Object} [options]
 * @param {Object} [options.data] - Resets the internally managed data to this when when it changes
 * @param {Object} [options.initialData] - An initial value of the internally managed data
 * @param {Object} [options.initialErrors] - An initial value of the internally managed errors
 * @param {Object} [options.validators] - Validators for the data structure for the set of fields,
 *   can also be controlled field-by-field
 * @param {function} [options.onChange] - A function to call when the data changes
 * @param {string} [options.name] - The name to pass to onChange, used to sync between levels of
 *   this structure
 * @returns {[FormContext.Provider, Object, {
 *   unregisterValidators: function,
 *   data: Object,
 *   onChange: function,
 *   registerValidators: function,
 *   unregisterSelfValidatingChild: function,
 *   errors: Object,
 *   registerSelfValidatingChild: function,
 *   validate: function,
 *   resetData: function,
 *   resetErrors: function
 * }]}
 */
const useValidation = ({
  data: dataProp,
  initialData = emptyObject,
  initialErrors = emptyObject,
  validators: validatorsProp,
  onChange: onChangeProp,
  name: nameProp = ''
} = {}) => {
  const [state, setState] = useState({
    data: dataProp || initialData,
    errors: initialErrors
  });
  const [runningValidator, setRunningValidator] = useState(null);
  const skipNotify = useRef(false);

  const registeredValidators = useRef({});
  const childSelfValidators = useRef({});

  const resetData = useCallback(
    () => setState(prevState => ({
      ...prevState,
      data: dataProp || initialData
    })),
    [dataProp, initialData]
  );
  const resetErrors = useCallback(
    () => setState(prevState => ({
      ...prevState,
      errors: initialErrors
    })),
    [initialErrors]
  );

  // When the data option changes, we need to reset our data to it
  useEffect(() => {
    if (dataProp && dataProp !== state.data) {
      skipNotify.current = true;
      resetData();
    }
  }, [dataProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // When our data (primarily) changes, we should notify the caller through the onChange prop
  // This is primarily used to sync FormSections to the root controller as the hook returns the data
  // and causes a rerender when it changes
  useEffect(() => {
    if (skipNotify.current) {
      skipNotify.current = false;
      return;
    }

    if (onChangeProp) {
      onChangeProp(nameProp, state.data);
    }
  }, [nameProp, onChangeProp, state.data]);

  useEffect(() => {
    if (!runningValidator) {
      return undefined;
    }

    let ignore = false;
    let calledBack = false;

    const {
      promise,
      name,
      silent,
      childErrors,
      childrenValid,
      callback
    } = runningValidator;

    promise.then(nextErrors => {
      if (ignore) {
        return;
      }

      setState((prevState) => {
        const mergedErrors = {
          ...prevState.errors,
          ...name ? { [name]: undefined } : {},
          ...nextErrors,
          ...childErrors
        };

        const nextState = {
          errors: mergedErrors,
          isValid: childrenValid && getIsValid(mergedErrors)
        };

        callback(nextState);
        calledBack = true;

        if (silent) {
          return prevState;
        }

        setRunningValidator(null);

        return {
          ...prevState,
          ...nextState
        };
      });
    });

    return () => {
      ignore = true;

      if (callback && !calledBack) {
        callback({ isValid: false });
      }
    };
  }, [runningValidator]);

  // Takes field-by-field validators and links them back to this so we can validate the whole form
  const unregisterValidators = useCallback((name) => {
    delete registeredValidators.current[name];

    setState(prevState => {
      const nextErrors = { ...prevState.errors };
      delete nextErrors[name];
      return ({
        ...prevState,
        errors: nextErrors
      });
    });
  }, []);
  const registerValidators = useCallback((name, validators) => {
    registeredValidators.current[name] = validators;

    /** @deprecated */
    return () => unregisterValidators(name);
  }, [unregisterValidators]);

  // Used by FormSection and ListFormSection to link validation back to the root controller,
  // can also be used for complex inputs
  const unregisterSelfValidatingChild = useCallback((name) => {
    delete childSelfValidators.current[name];
  }, []);
  const registerSelfValidatingChild = useCallback((name, fn) => {
    childSelfValidators.current[name] = fn;
    return () => unregisterSelfValidatingChild(name);
  }, [unregisterSelfValidatingChild]);

  // Used by inputs and sections to update our state and call field-by-field validation
  const onChange = useCallback((name, value, validate) => {
    setState((prevState) => {
      const validators = { ...validatorsProp, ...registeredValidators.current };
      const nextData = {
        ...prevState.data,
        [name]: value
      };

      if (validate) {
        generateErrors(validators, nextData, name)
          .then((errors) => {
            setState((morePrevState) => (morePrevState.data[name] === value ? {
              ...morePrevState,
              errors: {
                ...morePrevState.errors,
                [name]: undefined,
                ...errors
              }
            } : morePrevState));
          });
      }

      return {
        ...prevState,
        data: nextData
      };
    });
  }, [validatorsProp]);

  // Validates the entire field or just the section/field at `name`
  const validate = useCallback((name, silent) => new Promise(resolve => {
    const order = Object.keys(childSelfValidators.current);

    // If we have a section or complex input that validates itself, we need to collect those values
    // before running our own validation
    const selfValidators = name
      ? (childSelfValidators.current[name] ? [childSelfValidators.current[name](null, silent)] : [])
      : order.map(key => childSelfValidators.current[key](null, silent));
    Promise.all(selfValidators)
      .then(orderedResults => {
        const {
          data: childData,
          errors: childErrors,
          isValid: childrenValid
        } = orderedResults.reduce(({ data, errors, isValid }, value, index) => {
          const key = order[index];

          // Converts the array results of promise.all back into a matching format to our state
          return {
            data: {
              ...data,
              [key]: value.data
            },
            errors: {
              ...errors,
              [key]: value.errors
            },
            isValid: isValid && value.isValid
          };
        }, {
          data: {},
          errors: {},
          isValid: true
        });

        // Updates our state by merging the child results in and running our validations
        setState((prevState) => {
          const validators = { ...validatorsProp, ...registeredValidators.current };

          setRunningValidator({
            promise: generateErrors(validators, prevState.data, name),
            name,
            silent,
            childErrors,
            childrenValid,
            callback: (validationResult) => resolve({
              ...prevState,
              data: {
                ...prevState.data,
                ...childData
              },
              ...validationResult
            })
          });

          return {
            ...prevState,
            data: {
              ...prevState.data,
              ...childData
            }
          };
        });
      });
  }), [validatorsProp]);

  // If we don't memoize this, it will change on each call, causing everything to rerender
  return useMemo(() => {
    const value = {
      errors: state.errors,
      data: state.data,
      onChange,
      registerValidators,
      unregisterValidators,
      registerSelfValidatingChild,
      unregisterSelfValidatingChild,
      validate,
      resetData,
      resetErrors
    };

    return [FormContext.Provider, value, value];
  }, [
    onChange, validate, state.data, state.errors, resetData, resetErrors,
    // These don't ever change but lint complains about them
    unregisterSelfValidatingChild, unregisterValidators,
    registerSelfValidatingChild, registerValidators
  ]);
};

export default useValidation;
